import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fmtError, fmtEUR, fmtDate, isoDateInTz } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { Clock, Sparkles, Loader2, CheckCircle2, Euro, User } from "lucide-react";

export default function BookPage() {
  const { slug, api, info } = useTenant();
  // Date e orari sono quelli del CENTRO, non del telefono della cliente
  const tz = info?.timezone || "Europe/Rome";
  const dateTouched = useRef(false);
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [selectedSvc, setSelectedSvc] = useState(null);
  const [team, setTeam] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null); // null = qualsiasi operatrice
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [slots, setSlots] = useState(null); // null = non caricati, [] = giorno pieno/chiuso
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!api) return;
    api.listServices().then(setServices).catch((e) => setError(fmtError(e)));
  }, [api]);

  // operatrici: solo quelle che eseguono il trattamento scelto (se il centro
  // ha indicato le competenze); se quella scelta non lo fa, si torna a "qualsiasi"
  useEffect(() => {
    if (!api) return;
    api.team(selectedSvc?.id).then((t) => {
      setTeam(t);
      setSelectedStaff((cur) => (cur && !t.some((m) => m.id === cur.id && m.active !== false) ? null : cur));
    }).catch(() => setTeam([]));
  }, [api, selectedSvc]);

  // default: domani, nel calendario del centro (non la data UTC)
  useEffect(() => {
    if (!dateTouched.current) setDate(isoDateInTz(tz, 1));
  }, [tz]);

  // ogni volta che cambiano servizio, operatrice o data, ricarica gli slot liberi
  useEffect(() => {
    if (!api || !selectedSvc || !date) { setSlots(null); return; }
    setLoadingSlots(true);
    setTime("");
    api.availability(selectedSvc.id, date, selectedStaff?.id)
      .then((r) => setSlots(r.slots))
      .catch((e) => { setSlots(null); setError(fmtError(e)); })
      .finally(() => setLoadingSlots(false));
  }, [api, selectedSvc, selectedStaff, date]);

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedSvc) { setError("Seleziona un trattamento"); return; }
    if (!time) { setError("Scegli un orario tra quelli disponibili"); return; }
    setSubmitting(true);
    setError("");
    try {
      // Lo slot "HH:MM" è l'ora del centro: si invia così com'è, senza fuso,
      // e il server la interpreta col fuso del centro. Convertirla col fuso del
      // telefono spostava l'appuntamento (es. +1 ora da Londra).
      const appt = await api.book(selectedSvc.id, `${date}T${time}:00`, notes || null, selectedStaff?.id);
      setSuccess(appt);
    } catch (err) {
      setError(fmtError(err));
      // lo slot potrebbe essere stato preso nel frattempo: ricarica
      if (err?.response?.status === 409 && selectedSvc && date) {
        api.availability(selectedSvc.id, date, selectedStaff?.id).then((r) => setSlots(r.slots)).catch(() => {});
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div data-testid="book-success" className="max-w-xl">
        <div className="rounded-3xl border border-black/5 bg-white p-10 text-center">
          <div className="mx-auto h-14 w-14 rounded-full bg-gradient-to-br from-[#A78BFA] to-[#67E8F9] flex items-center justify-center mb-5">
            <CheckCircle2 className="text-white" size={26} />
          </div>
          <h2 className="font-serif text-3xl text-[#1A1A1A]">Appuntamento confermato ✨</h2>
          <p className="mt-3 text-[#4A4A4A] font-light">
            {success.service_name} il <strong>{fmtDate(success.starts_at, tz)}</strong>
            {success.staff_name && <> con <strong>{success.staff_name}</strong></>}
          </p>
          <div className="mt-7 flex justify-center gap-3 flex-wrap">
            <button
              onClick={() => navigate(`/${slug}/app/appointments`)}
              data-testid="book-success-home"
              className="bg-[#1A1A1A] text-white rounded-full px-6 py-3 text-sm hover:bg-[#333]"
            >
              I miei appuntamenti
            </button>
            <button
              onClick={() => { setSuccess(null); setSelectedSvc(null); }}
              data-testid="book-success-again"
              className="bg-white border border-black/10 text-[#1A1A1A] rounded-full px-6 py-3 text-sm hover:bg-[#FAFAFA]"
            >
              Prenota un altro
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="book-page">
      <div className="text-[11px] tracking-[0.3em] uppercase text-[#A78BFA] mb-3">Prenota</div>
      <h1 className="font-serif font-light text-4xl md:text-5xl tracking-tight text-[#1A1A1A]">
        Scegli il tuo <span className="italic">trattamento.</span>
      </h1>

      <div className="mt-10 grid lg:grid-cols-3 gap-5">
        {services.map((s) => (
          <button
            key={s.id}
            data-testid={`service-card-${s.id}`}
            type="button"
            onClick={() => setSelectedSvc(s)}
            className={`text-left rounded-3xl p-7 border transition-all tilt-card ${
              selectedSvc?.id === s.id
                ? "bg-[#1A1A1A] text-white border-[#1A1A1A]"
                : "bg-white border-black/5 hover:border-[#A78BFA]/40"
            }`}
          >
            <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase opacity-70">
              <Sparkles size={12} /> Trattamento
            </div>
            <div className="mt-3 font-serif text-2xl">{s.name}</div>
            <div className={`mt-1 text-sm font-light ${selectedSvc?.id === s.id ? "text-white/80" : "text-[#4A4A4A]"}`}>
              {s.description}
            </div>
            <div className={`mt-5 flex items-center gap-4 text-sm ${selectedSvc?.id === s.id ? "text-white/80" : "text-[#4A4A4A]"}`}>
              <span className="inline-flex items-center gap-1.5"><Clock size={14} /> {s.duration_minutes} min</span>
              <span className="inline-flex items-center gap-1.5"><Euro size={14} /> {fmtEUR(s.price)}</span>
              <span className="ml-auto text-xs">+{s.points_reward} pt</span>
            </div>
          </button>
        ))}
      </div>

      <form onSubmit={submit} className="mt-12 max-w-2xl bg-white border border-black/5 rounded-3xl p-8" data-testid="book-form">
        <h2 className="font-serif text-2xl text-[#1A1A1A] mb-6">Quando vieni a trovarci?</h2>

        {team.length > 0 && (
          <div className="mb-6">
            <span className="block text-[10px] tracking-[0.25em] uppercase text-[#737373] mb-2">Con chi?</span>
            <div className="flex flex-wrap gap-2" data-testid="staff-grid">
              <button
                type="button"
                data-testid="staff-any"
                onClick={() => setSelectedStaff(null)}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm border transition-all ${
                  !selectedStaff
                    ? "bg-[#1A1A1A] text-white border-[#1A1A1A]"
                    : "bg-[#FAFAFA] border-black/10 hover:border-[#A78BFA]"
                }`}
              >
                <Sparkles size={13} /> Qualsiasi operatrice
              </button>
              {team.map((m) => (
                m.active === false ? (
                  <span
                    key={m.id}
                    data-testid={`staff-${m.id}`}
                    title="Operatrice non disponibile al momento"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm border border-black/5 bg-[#F5F5F5] text-[#A3A3A3] cursor-not-allowed select-none"
                  >
                    <User size={13} /> {m.name}
                    <span className="text-[10px] uppercase tracking-wide">non disponibile</span>
                  </span>
                ) : (
                  <button
                    key={m.id}
                    type="button"
                    data-testid={`staff-${m.id}`}
                    onClick={() => setSelectedStaff(m)}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm border transition-all ${
                      selectedStaff?.id === m.id
                        ? "bg-[#1A1A1A] text-white border-[#1A1A1A]"
                        : "bg-[#FAFAFA] border-black/10 hover:border-[#A78BFA]"
                    }`}
                  >
                    <User size={13} /> {m.name}
                  </button>
                )
              ))}
            </div>
          </div>
        )}

        <label className="block max-w-xs">
          <span className="block text-[10px] tracking-[0.25em] uppercase text-[#737373] mb-2">Data</span>
          <input
            data-testid="book-date"
            type="date"
            required
            value={date}
            min={isoDateInTz(tz, 0)}
            onChange={(e) => { dateTouched.current = true; setDate(e.target.value); }}
            className="w-full bg-[#FAFAFA] border border-black/10 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:border-[#A78BFA]"
          />
        </label>

        <div className="mt-5">
          <span className="block text-[10px] tracking-[0.25em] uppercase text-[#737373] mb-2">Orari disponibili</span>
          {!selectedSvc ? (
            <div className="text-sm text-[#737373]" data-testid="slots-pick-service">
              Seleziona prima un trattamento per vedere gli orari liberi.
            </div>
          ) : loadingSlots ? (
            <div className="flex items-center gap-2 text-sm text-[#737373]" data-testid="slots-loading">
              <Loader2 size={14} className="animate-spin" /> Controllo la disponibilità…
            </div>
          ) : slots && slots.length > 0 ? (
            <div className="flex flex-wrap gap-2" data-testid="slots-grid">
              {slots.map((s) => (
                <button
                  key={s}
                  type="button"
                  data-testid={`slot-${s}`}
                  onClick={() => setTime(s)}
                  className={`px-4 py-2 rounded-full text-sm border transition-all ${
                    time === s
                      ? "bg-[#1A1A1A] text-white border-[#1A1A1A]"
                      : "bg-[#FAFAFA] border-black/10 hover:border-[#A78BFA]"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          ) : (
            <div className="text-sm text-[#737373]" data-testid="slots-empty">
              {selectedStaff
                ? `${selectedStaff.name} non ha orari liberi in questa data: prova un altro giorno o scegli un'altra operatrice.`
                : "Nessun orario libero in questa data: il centro è chiuso o al completo. Prova un altro giorno."}
            </div>
          )}
        </div>
        <label className="block mt-4">
          <span className="block text-[10px] tracking-[0.25em] uppercase text-[#737373] mb-2">Note (opzionale)</span>
          <textarea
            data-testid="book-notes"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Allergie, preferenze, ecc."
            className="w-full bg-[#FAFAFA] border border-black/10 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:border-[#A78BFA] resize-none"
          />
        </label>

        {error && (
          <div data-testid="book-error" className="mt-4 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] rounded-2xl px-4 py-2.5">
            {error}
          </div>
        )}

        <button
          type="submit"
          data-testid="book-submit"
          disabled={submitting || !selectedSvc || !time}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#1A1A1A] hover:bg-[#333] disabled:opacity-50 text-white rounded-full px-6 py-3.5 text-sm tracking-wide transition-all"
        >
          {submitting && <Loader2 size={16} className="animate-spin" />}
          {!selectedSvc ? "Seleziona un trattamento"
            : !time ? "Scegli un orario"
            : `Prenota ${selectedSvc.name} alle ${time}`}
        </button>
      </form>
    </div>
  );
}
